import { useState, type FormEvent } from "react";
import { LogOut } from "lucide-react";
import moment from "moment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  defaultVendorOffSiteTime,
  isVendorOffSiteEligible,
  vendorOffSiteTimeWarning,
} from "@/lib/utils/daily-work-vendor-off-site";

/**
 * Vendor-only "Mark off-site" for a stuck day (SHY-193): the worker is on-site
 * with no off-site mark. Offers the scheduled end (or now) as the time and an
 * optional note, then hands both to the caller. Renders nothing when the day
 * is not in that state.
 */

type Props = {
  execution:
    | { clock_in: string | null; clock_out: string | null }
    | null
    | undefined;
  dayStatus: string;
  maxHours: number | null | undefined;
  onConfirm: (input: {
    clockOut: string;
    note: string | null;
  }) => Promise<void>;
};

export default function MarkOffSiteForm({
  execution,
  dayStatus,
  maxHours,
  onConfirm,
}: Props) {
  const [open, setOpen] = useState(false);
  const [clockOut, setClockOut] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isVendorOffSiteEligible(execution, dayStatus)) return null;

  // An earlier close may have written the mark and then failed to complete
  // the day; that mark is kept and only the status step is retried.
  const existingClockOut = execution?.clock_out
    ? execution.clock_out.slice(0, 5)
    : null;
  const warning = existingClockOut
    ? null
    : vendorOffSiteTimeWarning(execution?.clock_in, clockOut);

  const openForm = () => {
    setClockOut(
      existingClockOut ??
        defaultVendorOffSiteTime({
          clockIn: execution?.clock_in ?? null,
          maxHours,
          now: moment().format("HH:mm"),
        }),
    );
    setNote("");
    setOpen(true);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!clockOut || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm({ clockOut, note: note.trim() || null });
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <div
        className="rounded-md border border-orange-200 bg-orange-50 p-3 text-sm"
        data-testid="daily-work-off-site-prompt"
      >
        <p className="text-orange-800">
          {existingClockOut
            ? "Off-site was recorded but this day never completed. Finish closing it."
            : "The worker is still marked on-site. Close this day on their behalf."}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={openForm}
          data-testid="daily-work-mark-off-site"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Mark off-site
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded-md border p-3 text-sm"
      data-testid="daily-work-off-site-form"
    >
      <div className="space-y-1">
        <Label htmlFor="daily-work-off-site-time">Off-site time</Label>
        <Input
          id="daily-work-off-site-time"
          type="time"
          value={clockOut}
          onChange={(e) => setClockOut(e.target.value)}
          required
          disabled={existingClockOut !== null}
          data-testid="daily-work-off-site-time"
        />
        {warning && (
          <p
            className="text-xs text-amber-700"
            data-testid="daily-work-off-site-warning"
          >
            {warning}
          </p>
        )}
      </div>
      {!existingClockOut && (
        <div className="space-y-1">
          <Label htmlFor="daily-work-off-site-note">Note (optional)</Label>
          <Textarea
            id="daily-work-off-site-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why the worker did not mark off-site"
            rows={2}
            data-testid="daily-work-off-site-note"
          />
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        This records the off-site time in the activity log as marked by you and
        completes the day.
      </p>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setOpen(false)}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={submitting || !clockOut}
          data-testid="daily-work-off-site-confirm"
        >
          {submitting ? "Saving..." : "Confirm off-site"}
        </Button>
      </div>
    </form>
  );
}
